import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { API_BASE_URL } from '../tokens/api-base-url.token';
import { FEEDBACK_API_TOKEN_REFRESH_ENABLED } from '../tokens/token-refresh.token';
import {
  DEFAULT_FEEDBACK_API_TOKEN_STORAGE_KEY,
  FEEDBACK_API_TOKEN_STORAGE_KEY,
} from '../tokens/token-storage-key.token';

export interface FeedbackApiConfig {
  /**
   * Origin of the Feedback API host, without the `/api/v1` prefix or a trailing
   * slash. Example: `http://localhost:5002`.
   */
  baseUrl: string;

  /**
   * localStorage key the bearer token is read from by the auth interceptor.
   * Defaults to `id_token_delegate_feedback-api`.
   */
  tokenStorageKey?: string;

  /**
   * Allow the auth interceptor to recover from a `401` by fetching a fresh
   * token from `GET /v1/token` and retrying the request once. Defaults to
   * false. Enable only in hosts entitled to a wildcard token (the main app),
   * not in the embeddable widget, which carries a round-scoped token.
   */
  refreshTokenOn401?: boolean;
}

/**
 * Registers the Feedback API configuration. Add to the application's
 * `providers` array alongside `provideHttpClient()`. To attach the bearer
 * token automatically, include `feedbackApiAuthInterceptor` in
 * `provideHttpClient(withInterceptors([...]))`.
 *
 * @example
 * bootstrapApplication(App, {
 *   providers: [
 *     provideHttpClient(withFetch(), withInterceptors([feedbackApiAuthInterceptor])),
 *     provideFeedbackApi({ baseUrl: environment.apiBaseUrl }),
 *   ],
 * });
 */
export function provideFeedbackApi(config: FeedbackApiConfig): EnvironmentProviders {
  const normalizedBaseUrl = config.baseUrl.replace(/\/+$/, '');
  return makeEnvironmentProviders([
    { provide: API_BASE_URL, useValue: normalizedBaseUrl },
    {
      provide: FEEDBACK_API_TOKEN_STORAGE_KEY,
      useValue: config.tokenStorageKey ?? DEFAULT_FEEDBACK_API_TOKEN_STORAGE_KEY,
    },
    {
      provide: FEEDBACK_API_TOKEN_REFRESH_ENABLED,
      useValue: config.refreshTokenOn401 ?? false,
    },
  ]);
}
